import React, { useState, useEffect } from 'react';
import api from '../../api/auth';

const StudentBulletins = () => {
  const [bulletins, setBulletins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    fetchBulletins();
  }, []);

  const fetchBulletins = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await api.get('/student/report-cards', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBulletins(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };

  const getAverageColor = (avg) => {
    if (avg >= 16) return 'text-green-600';
    if (avg >= 14) return 'text-blue-600';
    if (avg >= 12) return 'text-yellow-600';
    if (avg >= 10) return 'text-orange-600';
    return 'text-red-600';
  };

  const getDecisionColor = (decision) => {
    switch (decision) {
      case 'admis': return 'bg-green-100 text-green-700 border-green-500';
      case 'redouble': return 'bg-red-100 text-red-700 border-red-500';
      case 'risque_redoublement': return 'bg-orange-100 text-orange-700 border-orange-500';
      case 'a_examiner': return 'bg-yellow-100 text-yellow-700 border-yellow-500';
      case 'en_attente': return 'bg-gray-100 text-gray-700 border-gray-500';
      default: return 'bg-gray-50 text-gray-500 border-gray-300';
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[#2C3E50]">📄 Mes bulletins</h1>
        <p className="text-gray-500">Consultez vos bulletins trimestriels</p>
      </div>

      <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-4">
        <p className="text-sm text-blue-700">
          ℹ️ <strong>Information :</strong> Seuls les bulletins validés par l'administration apparaissent ici.
        </p>
      </div>

      {loading ? (
        <div className="bg-white rounded-xl shadow-md p-8 text-center">
          <div className="animate-pulse space-y-3">
            <div className="h-4 bg-gray-200 rounded w-1/4 mx-auto"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto"></div>
          </div>
        </div>
      ) : bulletins.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md p-12 text-center">
          <p className="text-4xl mb-3">📭</p>
          <p className="text-lg font-medium text-gray-700 mb-2">Aucun bulletin disponible</p>
          <p className="text-sm text-gray-500">
            Vos bulletins apparaîtront ici dès qu'ils seront validés par l'administration.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {bulletins.map((b) => (
            <div
              key={b.id}
              className="bg-white rounded-xl shadow-md p-5 border-l-4 border-[#E67E22] cursor-pointer hover:shadow-lg transition"
              onClick={() => setSelected(b)}
            >
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className="text-lg font-bold text-[#2C3E50]">📄 {b.period_label}</h3>
                  <p className="text-sm text-gray-500">{b.academic_year} — {b.class_name}</p>
                </div>
                <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">🟢 Validé</span>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="bg-blue-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Moyenne</p>
                  <p className={`text-2xl font-bold ${getAverageColor(b.general_average)}`}>
                    {b.general_average?.toFixed(2)}/20
                  </p>
                </div>
                <div className="bg-green-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Rang</p>
                  <p className="text-2xl font-bold text-green-600">
                    {b.rank}/{b.total_students}
                  </p>
                </div>
              </div>

              {b.decision_label && (
                <div className={`mt-3 border-l-4 rounded-lg p-2 ${getDecisionColor(b.decision)}`}>
                  <p className="text-xs font-bold">🎓 {b.decision_label}</p>
                </div>
              )}

              <p className="text-xs text-gray-500 mt-3">Publié le {b.published_at}</p>
            </div>
          ))}
        </div>
      )}

      {/* Modal détail */}
      {selected && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl p-6 w-full max-w-3xl my-8">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h2 className="text-xl font-bold text-[#2C3E50]">📄 Bulletin {selected.period_label}</h2>
                <p className="text-sm text-gray-500">{selected.academic_year} — {selected.class_name}</p>
              </div>
              <button
                onClick={() => setSelected(null)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="bg-blue-50 rounded-lg p-4 text-center">
                <p className="text-xs text-gray-500">Moyenne générale</p>
                <p className={`text-3xl font-bold ${getAverageColor(selected.general_average)}`}>
                  {selected.general_average?.toFixed(2)}/20
                </p>
              </div>
              <div className="bg-green-50 rounded-lg p-4 text-center">
                <p className="text-xs text-gray-500">Rang</p>
                <p className="text-3xl font-bold text-green-600">
                  {selected.rank}/{selected.total_students}
                </p>
              </div>
            </div>

            <h3 className="font-bold text-[#2C3E50] mb-2">📚 Détail par matière</h3>
            <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg mb-4">
              <table className="w-full">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600">Matière</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600">Coef</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600">Moyenne</th>
                  </tr>
                </thead>
                <tbody>
                  {(selected.data?.subjects || []).map((s, i) => (
                    <tr key={i} className="border-t">
                      <td className="py-2 px-3">{s.subject_name}</td>
                      <td className="py-2 px-3 text-sm">{s.coefficient}</td>
                      <td className={`py-2 px-3 font-bold ${getAverageColor(s.average)}`}>
                        {s.average}/20
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {selected.appreciation && (
              <div className="bg-orange-50 rounded-lg p-3 mb-4">
                <p className="text-xs font-bold text-[#E67E22] mb-1">💬 Appréciation</p>
                <p className="text-sm text-gray-700 italic">"{selected.appreciation}"</p>
              </div>
            )}

            {selected.decision_label && (
              <div className={`border-l-4 rounded-lg p-4 mb-4 ${getDecisionColor(selected.decision)}`}>
                <p className="text-xs font-bold mb-1">🎓 DÉCISION DE FIN D'ANNÉE</p>
                <p className="text-lg font-bold">{selected.decision_label}</p>
                {selected.next_class_name && (
                  <p className="text-sm mt-1">Classe suivante : <strong>{selected.next_class_name}</strong></p>
                )}
                {selected.decision_comment && (
                  <p className="text-xs italic mt-2">"{selected.decision_comment}"</p>
                )}
                {selected.decision_date && (
                  <p className="text-xs mt-2">Décision du {selected.decision_date}</p>
                )}
              </div>
            )}

            <div className="flex justify-end">
              <button
                onClick={() => setSelected(null)}
                className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentBulletins;